import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { formatMoney, noop } from '@ps-gui/shared-utils';
import { parseCurrency } from '../utils/Parser.utils';

@Directive({
  selector: 'input[ctExtractionCurrencyInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputDirective),
      multi: true,
    },
  ],
})
export class CurrencyInputDirective implements ControlValueAccessor {
  private onTouched: () => void = noop;

  private readonly element = inject(ElementRef);

  onChange: (value: unknown) => void = noop;

  writeValue(value: number | string | null): void {
    this.element.nativeElement.value = this.getDisplayValue(value);
  }

  private getDisplayValue(value: number | string | null) {
    if (value === null || value === '') {
      return '';
    }

    const parsedValue = typeof value === 'number' ? value : parseCurrency(String(value));
    const maxDecimalDigits = this.getNeededMaximumDigits(String(parsedValue));
    return this.modifyValue(parsedValue, maxDecimalDigits);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  @HostListener('input', ['$event.target'])
  handleInput(eventTarget: EventTarget | null): void {
    const inputElement = eventTarget as HTMLInputElement;
    const value = inputElement.value;
    this.onChange(value === '' ? null : this.revertValue(value));
  }

  @HostListener('blur')
  handleBlur(): void {
    this.writeValue(this.element.nativeElement.value);
    this.onTouched();
  }

  private modifyValue(value: number | null, maxDecimalDigits: number): string | null {
    return value !== null ? formatMoney(value, false, maxDecimalDigits) : null;
  }

  private revertValue(value: string): number | null {
    return parseCurrency(value);
  }

  private getNeededMaximumDigits(value: string): number {
    return Math.max(value.split('.')[1]?.length ?? 0, 2);
  }
}
